import { useState, useMemo } from 'react';
import { ArrowLeftRight, AlertCircle } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';

/** Static reference rates (relative to USD). Replace with live API in prod. */
const RATES: Record<string, { rate: number; name: string }> = {
  USD: { rate: 1, name: 'US Dollar' },
  INR: { rate: 83.4, name: 'Indian Rupee' },
  EUR: { rate: 0.92, name: 'Euro' },
  GBP: { rate: 0.79, name: 'British Pound' },
  JPY: { rate: 149.8, name: 'Japanese Yen' },
  CAD: { rate: 1.36, name: 'Canadian Dollar' },
  AUD: { rate: 1.52, name: 'Australian Dollar' },
  SGD: { rate: 1.34, name: 'Singapore Dollar' },
  CHF: { rate: 0.88, name: 'Swiss Franc' },
  CNY: { rate: 7.22, name: 'Chinese Yuan' },
  AED: { rate: 3.67, name: 'UAE Dirham' },
  HKD: { rate: 7.82, name: 'Hong Kong Dollar' },
  KRW: { rate: 1340, name: 'Korean Won' },
  BRL: { rate: 5.04, name: 'Brazilian Real' },
  MXN: { rate: 17.05, name: 'Mexican Peso' },
};

const SYMBOLS: Record<string, string> = {
  USD: '$',
  INR: '₹',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: 'C$',
  AUD: 'A$',
  SGD: 'S$',
  CHF: 'CHF',
  CNY: '¥',
  AED: 'د.إ',
  HKD: 'HK$',
  KRW: '₩',
  BRL: 'R$',
  MXN: '$',
};

export function CurrencyConverter() {
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('INR');
  const [amount, setAmount] = useState(100);

  const result = useMemo(() => {
    const inUsd = amount / RATES[from].rate;
    return inUsd * RATES[to].rate;
  }, [amount, from, to]);

  const codes = Object.keys(RATES);

  return (
    <div>
      <ToolHeader
        toolId="currency-converter"
        title="Currency converter"
        description="Quick conversions across major currencies. Reference rates — refresh before any consequential decisions."
        badges={<Badge variant="warning">reference rates</Badge>}
      />

      <Card className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
          <div className="space-y-2">
            <Field label="From">
              <Select
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                options={codes.map((c) => ({ value: c, label: `${c} · ${RATES[c].name}` }))}
              />
            </Field>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted text-lg font-medium">
                {SYMBOLS[from]}
              </span>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value) || 0)}
                className="!pl-9 font-mono text-lg !h-12"
              />
            </div>
          </div>

          <button
            onClick={() => {
              const tmp = from;
              setFrom(to);
              setTo(tmp);
            }}
            className="size-10 rounded-lg border border-border hover:border-border-strong hover:bg-surface-hover flex items-center justify-center self-end mb-1.5 transition"
          >
            <ArrowLeftRight size={14} />
          </button>

          <div className="space-y-2">
            <Field label="To">
              <Select
                value={to}
                onChange={(e) => setTo(e.target.value)}
                options={codes.map((c) => ({ value: c, label: `${c} · ${RATES[c].name}` }))}
              />
            </Field>
            <div className="!h-12 px-3 flex items-center gap-2 rounded-lg bg-bg-inset border border-border font-mono text-lg tabular-nums">
              <span className="text-fg-muted">{SYMBOLS[to]}</span>
              {result.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <div className="mt-3 text-xs text-fg-subtle">
          1 {from} = {(RATES[to].rate / RATES[from].rate).toFixed(4)} {to}
        </div>
      </Card>

      {/* Quick conversions */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {codes
          .filter((c) => c !== from)
          .slice(0, 6)
          .map((c) => {
            const v = (amount / RATES[from].rate) * RATES[c].rate;
            return (
              <button
                key={c}
                onClick={() => setTo(c)}
                className="text-left p-3 rounded-lg bg-surface border border-border hover:border-border-strong transition"
              >
                <div className="text-2xs uppercase tracking-wider text-fg-subtle">
                  {RATES[c].name}
                </div>
                <div className="text-sm font-mono mt-0.5 tabular-nums">
                  {SYMBOLS[c]} {v.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
              </button>
            );
          })}
      </div>
    </div>
  );
}

export default CurrencyConverter;
